import React from "react";
import { FileText, Scroll } from "lucide-react";
import { Link } from "react-router-dom";
import { Course } from "../../types/Course";

export interface CourseCardComponentProps extends Omit<
  Course,
  "deals" | "entryRequirements" | "scholarships"
> {
  deals: Course["deals"];
  entryRequirements?: Course["entryRequirements"];
  scholarships?: Course["scholarships"];
  uniImg: string;
}

export default function CourseCard({
  uniImg,
  courseName,
  universityName,
  universityLocation,
  duration,
  intake,
  deals,
  tuitionFee,
  courseSlug,
  country,
}: CourseCardComponentProps) {
  const totalDeals = Array.isArray(deals)
    ? deals.reduce((sum, deal) => sum + (Number(deal.amount) || 0), 0)
    : 0;

  return (
    <Link
      to={`/courses/${courseSlug}`}
      className="flex flex-col md:flex-row w-full shadow hover:shadow-md text-gray-600"
    >
      <div className="flex md:w-3/4 border-b-2 lg:border-r-2 border-gray-200">
        <div className="hidden md:flex w-20 md:w-1/3 items-center justify-center shadow-lg p-4">
          <img src={uniImg} alt="uniLogo" />
        </div>
        <div className="flex flex-col gap-2 md:gap-3 p-2 md:p-3 text-sm md:w-2/3">
          <p className="font-semibold text-base md:text-xl">{courseName}</p>
          <p className="text-sm">
            {universityName} | {country}
          </p>

          <div className="hidden md:flex flex-col gap-2">
            <div className="flex justify-between text-sm">
              <p className="flex gap-1 items-center">
                <FileText className="w-8 h-8 text-red-500" />
                Entry Requirements
              </p>
              <p className="flex gap-1 items-center ">
                <Scroll className="w-8 h-8 text-red-500" />
                Merit Scholarship
              </p>
            </div>
            {totalDeals > 0 && <p>Offer worth MYR {totalDeals}</p>}
          </div>
        </div>
      </div>

      <div className="w-full justify-between md:w-1/4 p-2 md:p-3 flex flex-row md:flex-col gap-2">
        <div className="flex flex-col gap-2 md:hidden text-xs">
          <div className="flex flex-col gap-2">
            <p className="flex gap-2 items-center">
              <FileText className="w-4 h-4 text-red-500" />
              Entry Requirements
            </p>
            <p className="flex gap-2 items-center">
              <Scroll className="w-4 h-4 text-red-500" />
              Merit Scholarship
            </p>
          </div>
          <p>Offer worth MYR 300</p>
          <div className="leading-none">
            <p className="text-xs">Intake</p>
            <ul className="grid grid-cols-4 font-semibold">
              {intake?.map((intake) => (
                <li key={intake}>{intake}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <div className="hidden md:flex flex-col leading-none">
            <p className="text-xs">Duration</p>
            <p className="font-semibold">{duration} years</p>
          </div>
          <div className="hidden md:flex flex-col border-b-2 border-red-200 leading-none pb-2">
            <p className="text-xs">Intake</p>
            <ul className="grid grid-cols-4 place-items-center font-semibold">
              {intake?.map((intake) => (
                <li key={intake}>{intake}</li>
              ))}
            </ul>
          </div>
        </div>

        <div className="flex flex-col leading-none pb-2">
          <div className="flex items-baseline justify-between">
            <p className="text-xs">Total Fee</p>
            <p className="font-semibold">{tuitionFee?.toLocaleString()}</p>
          </div>
          <div className="flex items-baseline justify-between">
            <p className="text-xs">Deal</p>
            <p className="font-semibold">{totalDeals?.toLocaleString()}</p>
          </div>
          <div className="flex items-baseline justify-between border-b-2 border-red-200 pb-2">
            <p className="text-xs">Scholarship</p>
            <p className="font-semibold">?</p>
          </div>
          <div className="flex flex-col font-semibold leading-none">
            <p className="text-xs">MYR</p>
            <p className="text-4xl">
              {(tuitionFee - totalDeals).toLocaleString()}
            </p>
          </div>
        </div>
      </div>
    </Link>
  );
}
